import {
    Box,
    Button,
    CircularProgress,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { useSiteProfileData } from '../../../data/DataProviders/SiteProfileDataProvider';
import { isDataLoaded, SiteProfile, UserOperations } from '../../../types';
import { DataIndicators } from '../../../components/DataIndicators';
import { DeleteForever, Notes } from '@mui/icons-material';
import { Actions, Thunks } from '../../../actions';
import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from '../../../data/DataProviders/ToastProvider';
import { Selectors } from '../../../selectors';
import { useLanguageLocale } from '../../../hooks/language-locale-hooks';
import locationStyles from '../LocationStyle';
import { ConfirmationPopUp } from '../../../components/ConfirmationPopUp/ConfirmationPopUp';
import { useAuthHelper } from '../../../components/AuthContextProvider';
import { useSiteListData } from '../../../data/DataProviders/SiteListProvider';
import { pushAppRoute } from '../../../data/domain/route-manager';
import { useHistory } from 'react-router-dom';
//import { useUserRoles } from "../../../hooks/data-hooks";
import * as PXBColors from '@brightlayer-ui/colors';
import { SiteList } from '../../../components/SiteList/SiteList';
import { DeviceCountProvider } from '../../../data/DataProviders/DeviceCountProvider';
import { NavigationHeader } from '../../../components/NavigationHeader/NavigationHeader';
import { LabelWithSubTitle } from '../../../components/LabelWithSubTitle/LabelWithSubtitle';
import LocationOn from '@mui/icons-material/LocationOn';
import { useCurrentAppRoute } from '../../../hooks/app-route-hooks';
import { GenericToolbar } from '../../../components/GenericToolbar';
import { ContentContainer } from '../../../components/ContentContainer';
import { isRecordFound, useSelectedOrganizationData, useUserRoles } from '../../../hooks/data-hooks';

export const LocationProfileData: React.FC<{ data: SiteProfile }> = (props): JSX.Element => {
    const locationProfileData = useSiteProfileData();
    const classes = locationStyles();
    const dispatch = useDispatch();
    const { addToast } = useToasts();
    const selectedOrganization = useSelectedOrganizationData();
    const orgId = isRecordFound(selectedOrganization) ? selectedOrganization.value.id : '';
    const siteListData = useSiteListData();
    const history = useHistory();
    const siteState = useSelector(Selectors.Site.site);
    const { t } = useLanguageLocale();
    const authHelper = useAuthHelper();
    const userRole = useUserRoles();
    const currentAppRoute = useCurrentAppRoute();
    // const [alert, setAlert] = useState(false);
    const [locationData, setLocationData] = useState<SiteProfile | null>(null);

    const hasSites = isDataLoaded(siteListData) && siteListData.value.length > 0;
    const [openDelete, setOpenDelete] = React.useState(false);
    const locationId = (props.data && props.data.id) || '';

    useEffect(() => {
        if (props.data) setLocationData(props.data);
    }, [props.data]);
    useEffect(() => {
        if (!siteState.deleteSiteLoading && siteState.deleteSiteSuccess && isDataLoaded(locationProfileData)) {
            addToast('Location deleted successfully.');
            dispatch(Thunks.Dashboard.locationRefresh(true));
            pushAppRoute(history, { type: 'ManageLocation', orgId: locationProfileData.value.parentSiteId });
            dispatch(Actions.Site.SiteDeleteReset());
        }
    }, [siteState.deleteSiteLoading, siteState.deleteSiteSuccess]); //eslint-disable-line react-hooks/exhaustive-deps

    const navigateToEditLocation = (): void => {
        if (currentAppRoute.type === 'LocationProfile') {
            pushAppRoute(history, {
                type: 'EditLocation',
                orgId: currentAppRoute.orgId,
                locationId: currentAppRoute.locationId,
            });
        }
    };

    const hasDeleteAccess = isRecordFound(userRole)
        ? userRole.value.some((x) => x.operations.some((operation) => operation === UserOperations.DeleteLocation))
        : false;

    const deleteSite = (): void => {
        dispatch(Thunks.Site.deleteSite(locationId, addToast, authHelper));
        setOpenDelete(false);
    };
    const validateDelete = (): void => {
        if (hasSites)
            addToast(
                'You need to delete all Sites associated with this Location before you can delete this Location.',
                'error',
                'Alert'
            );
        else setOpenDelete(true);
    };
    const closePopUp = (): void => {
        setOpenDelete(false);
    };
    return (
        <>
            <GenericToolbar
                title={props.data.name}
                subtitle="Location Profile"
                showBackButton
                backButtonOnClick={(): void => {
                    pushAppRoute(history, {
                        type: 'ManageLocation',
                        orgId: orgId,
                    });
                }}
            />
            <ContentContainer>
                {!isDataLoaded(locationProfileData) && (
                    <DataIndicators
                        errorMessage="No Data"
                        emptyMessage="No Data"
                        data={locationProfileData}
                        emptyIcon="LocationCity"
                        isEmpty={false}
                    />
                )}

                {isDataLoaded(locationProfileData) && locationData && (
                    <>
                        <NavigationHeader
                            locationName={locationData.name}
                            siteName={''}
                            for={'location'}
                            connectedDevice=""
                            device={''}
                        />
                        <Paper>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={8}>
                                    <Box>
                                        <List>
                                            <ListItem
                                                alignItems="flex-start"
                                                style={{ paddingLeft: 24 }}
                                                data-testid="location-name"
                                            >
                                                <ListItemText>
                                                    <Typography className={classes.wrapText} variant="h6">
                                                        {locationData.name}
                                                    </Typography>
                                                </ListItemText>
                                            </ListItem>

                                            <ListItem data-testid="address" className={classes.margin8}>
                                                <ListItemIcon>
                                                    <LocationOn />
                                                </ListItemIcon>
                                                <LabelWithSubTitle
                                                    title={`${locationData.address}, ${locationData.city},${locationData.country}, ${locationData.state}, ${locationData.zipCode}`}
                                                    subTitle={'Address'}
                                                />
                                            </ListItem>

                                            <ListItem
                                                style={{ width: '70%' }}
                                                data-testid="location-notes"
                                                className={classes.margin8}
                                            >
                                                <ListItemIcon>
                                                    <Notes />
                                                </ListItemIcon>
                                                {locationData.description === '' ? (
                                                    <Typography variant="caption" style={{ fontStyle: 'italic' }}>
                                                        No Notes are available
                                                    </Typography>
                                                ) : (
                                                    <div>
                                                        <Typography
                                                            component="pre"
                                                            variant="body2"
                                                            display="inline"
                                                            style={{
                                                                lineHeight: 1.25,
                                                                fontWeight: 600,
                                                                whiteSpace: 'pre-wrap',
                                                            }}
                                                        >
                                                            {locationData.description}
                                                        </Typography>
                                                    </div>
                                                )}
                                            </ListItem>
                                        </List>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <List>
                                            <ListItem data-testid="device-details">
                                                <Button
                                                    style={{ marginRight: 15, width: 300 }}
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={<EditIcon />}
                                                    data-testid="edit-action"
                                                    onClick={(): void => {
                                                        navigateToEditLocation();
                                                    }}
                                                >
                                                    Edit Location
                                                </Button>
                                            </ListItem>
                                            {hasDeleteAccess && (
                                                <ListItem>
                                                    <Button
                                                        style={{
                                                            width: 300,
                                                            color: PXBColors.red[500],
                                                            borderColor: PXBColors.gray[100],
                                                        }}
                                                        variant="outlined"
                                                        onClick={(): void => validateDelete()}
                                                        startIcon={
                                                            siteState.deleteSiteLoading &&
                                                            !siteState.deleteSiteSuccess ? (
                                                                <CircularProgress
                                                                    style={{ color: PXBColors.red[500] }}
                                                                    size="2rem"
                                                                />
                                                            ) : (
                                                                <DeleteForever />
                                                            )
                                                        }
                                                        color="inherit"
                                                        data-testid="delete-action"
                                                    >
                                                        Delete Forever
                                                    </Button>
                                                </ListItem>
                                            )}
                                        </List>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                        <DeviceCountProvider siteId={locationProfileData.value.id}>
                            <SiteList
                                locationId={locationProfileData.value.id}
                                orgId={locationProfileData.value.parentSiteId}
                            />
                        </DeviceCountProvider>
                    </>
                )}

                {openDelete && (
                    <ConfirmationPopUp
                        title={'Delete This Location?'}
                        body={
                            <Typography variant="subtitle2" style={{ marginBottom: 30 }}>
                                {'This Location and all of its data will be deleted forever. Do you wish to proceed?'}
                            </Typography>
                        }
                        okButtonText={'Delete Forever'}
                        cancelButtonText={t('ACTIONS.CANCEL')}
                        show={openDelete}
                        closeDialog={closePopUp}
                        handleOk={(): void => {
                            deleteSite();
                        }}
                        backgroundColor={PXBColors.red[500]}
                        width="xs"
                        okButtonShow={true}
                        cancelButtonsShow={true}
                    ></ConfirmationPopUp>
                )}
            </ContentContainer>
        </>
    );
};
